import React from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Badge,
  Skeleton,
  SkeletonText,
  Avatar,
  Button,
} from '@chakra-ui/react';
import { PageWrapper } from '@/components/layout';
import { SEO } from '@/components/seo';
import { formatDistanceToNow } from 'date-fns';
import { FileText, MessageCircle, User, AlertCircle, Trash2, ArrowLeft, UtensilsCrossed } from 'lucide-react';
import AppHeader from '@/components/navigation/AppHeader';
import { useGetReports } from '@/api';
import { useNavigate, useRouter } from '@tanstack/react-router';
import type { Report, ReportReason } from '@/types/social';

const REASON_LABELS: Record<ReportReason, string> = {
  spam: 'Spam',
  harassment: 'Harassment',
  hate_speech: 'Hate Speech',
  violence: 'Violence',
  inappropriate: 'Inappropriate Content',
  misinformation: 'Misinformation',
  impersonation: 'Impersonation',
  other: 'Other',
};

function ReportCard({ report, onViewContent }: { report: Report; onViewContent: (report: Report) => void }) {
  const content = report.reportedContent;
  const isDeleted = content?.deleted || !content;
  const canNavigate = !isDeleted && (
    (content?.type === 'post' && content?.urlKey) ||
    (content?.type === 'comment' && content?.postUrlKey) ||
    (content?.type === 'user' && content?.userId)
  );

  const getStatusColors = (status: Report['status']): { bg: string; color: string } => {
    switch (status) {
      case 'pending':
        return { bg: 'yellow.500', color: 'gray.800' };
      case 'reviewed':
        return { bg: 'blue.500', color: 'white' };
      case 'resolved':
        return { bg: 'green.500', color: 'white' };
      case 'dismissed':
        return { bg: 'gray.500', color: 'white' };
      default:
        return { bg: 'gray.500', color: 'white' };
    }
  };

  const getContentTypeIcon = () => {
    switch (report.contentType) {
      case 'post':
        return <FileText size={20} />;
      case 'comment':
        return <MessageCircle size={20} />;
      case 'user':
        return <User size={20} />;
      case 'menu_item':
        return <UtensilsCrossed size={20} />;
      default:
        return <AlertCircle size={20} />;
    }
  };

  const getContentTypeLabel = () => {
    switch (report.contentType) {
      case 'post':
        return 'Post';
      case 'comment':
        return 'Comment';
      case 'user':
        return 'Member';
      case 'menu_item':
        return 'Menu Item';
      default:
        return 'Content';
    }
  };

  const renderContentPreview = () => {
    if (isDeleted) {
      return (
        <HStack spacing={2} color="gray.500" py={2}>
          <Trash2 size={16} />
          <Text fontSize="sm" fontStyle="italic">
            This content is no longer available
          </Text>
        </HStack>
      );
    }

    if (content?.type === 'user') {
      return (
        <HStack spacing={3} py={2}>
          <Avatar size="sm" name={content.name} src={content.photoUrl || undefined} />
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            {content.name}
          </Text>
        </HStack>
      );
    }

    if (content?.type === 'menu_item') {
      return (
        <HStack spacing={2} py={2}>
          <UtensilsCrossed size={16} color="var(--chakra-colors-gray-500)" />
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            {content.name}
          </Text>
        </HStack>
      );
    }

    // Post or Comment
    return (
      <Box py={2}>
        {content?.preview && (
          <Text fontSize="sm" color="gray.700" noOfLines={2} mb={1}>
            {content.preview}
          </Text>
        )}
        {content?.authorName && (
          <Text fontSize="xs" color="gray.500">
            {content.type === 'comment' ? 'Comment by ' : 'Posted by '}
            <Text as="span" fontWeight="medium">{content.authorName}</Text>
          </Text>
        )}
      </Box>
    );
  };

  return (
    <Box
      p={4}
      bg="white"
      borderRadius="lg"
      borderWidth="1px"
      _hover={{ boxShadow: 'md', bg: canNavigate ? 'gray.50' : 'white' }}
      transition="all 0.2s"
      cursor={canNavigate ? 'pointer' : 'default'}
      onClick={() => canNavigate && onViewContent(report)}
    >
      <VStack align="stretch" spacing={3}>
        {/* Header */}
        <HStack justify="space-between">
          <HStack spacing={3}>
            <Box color="gray.600">{getContentTypeIcon()}</Box>
            <VStack align="start" spacing={0}>
              <Text color={'gray.600'} fontSize="md" fontWeight="bold">
                Reported {getContentTypeLabel()}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
              </Text>
            </VStack>
          </HStack>
          <Badge {...getStatusColors(report.status)} fontSize="xs" px={3} py={1} borderRadius="full">
            {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
          </Badge>
        </HStack>

        {/* Content Preview */}
        <Box pl={8} borderLeftWidth="2px" borderLeftColor="gray.200">
          {renderContentPreview()}
        </Box>

        {/* Report Details */}
        <Box pl={8}>
          <VStack align="start" spacing={2}>
            <HStack>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Reason:
              </Text>
              <Text fontSize="sm" color="gray.600">
                {REASON_LABELS[report.reason]}
              </Text>
            </HStack>

            {report.description && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
                  Your description:
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  {report.description}
                </Text>
              </Box>
            )}

            {report.reviewedAt && (
              <Text fontSize="xs" color="gray.500">
                Reviewed {formatDistanceToNow(new Date(report.reviewedAt), { addSuffix: true })}
              </Text>
            )}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}

function ReportSkeleton() {
  return (
    <Box p={4} bg="white" borderRadius="lg" borderWidth="1px">
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between">
          <HStack spacing={3}>
            <Skeleton height="20px" width="20px" />
            <VStack align="start" spacing={2}>
              <Skeleton height="16px" width="120px" />
              <Skeleton height="12px" width="80px" />
            </VStack>
          </HStack>
          <Skeleton height="24px" width="80px" borderRadius="full" />
        </HStack>
        <Box pl={8}>
          <SkeletonText noOfLines={2} spacing={2} skeletonHeight="14px" />
        </Box>
        <SkeletonText mt={2} noOfLines={2} spacing={2} skeletonHeight="12px" />
      </VStack>
    </Box>
  );
}

export function Reports() {
  const navigate = useNavigate();
  const router = useRouter();
  const { data, isLoading } = useGetReports();
  const reports = data?.results ?? [];

  const handleViewContent = (report: Report) => {
    const content = report.reportedContent;
    if (!content || content.deleted) return;

    switch (content.type) {
      case 'post':
        if (content.urlKey) {
          navigate({ to: '/posts/$urlKey', params: { urlKey: content.urlKey } });
        }
        break;
      case 'comment':
        if (content.postUrlKey) {
          navigate({ to: '/posts/$urlKey', params: { urlKey: content.postUrlKey } });
        }
        break;
      case 'user':
        if (content.userId) {
          navigate({ to: '/profile/$userId', params: { userId: content.userId } });
        }
        break;
    }
  };

  return (
    <PageWrapper>
      <SEO title="Reports" />
      <AppHeader page={'reports'} />
      <Box minH="100vh">
        <Container maxW="container.md" py={6}>
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <HStack justify="space-between" align="center" px={2}>
              <Button
                leftIcon={<ArrowLeft size={22} />}
                variant="ghost"
                onClick={() => router.history.back()}
                size={{ base: 'sm', lg: 'md' }}
                color="white"
                _hover={{ bg: 'whiteAlpha.300' }}
                minH={'44px'}
                minW={'44px'}
              >
              </Button>
              <VStack align="center" spacing={0}>
                <Heading size="lg" color="white">Reports</Heading>
                <Text fontSize="sm" color="gray.400">
                  Track your submitted reports
                </Text>
              </VStack>
              <Box w="60px" /> {/* Spacer for center alignment */}
            </HStack>

            {/* Reports List */}
            <VStack spacing={4} align="stretch">
              {isLoading ? (
                <>
                  <ReportSkeleton />
                  <ReportSkeleton />
                  <ReportSkeleton />
                </>
              ) : reports.length === 0 ? (
                <Box
                  p={12}
                  bg="white"
                  borderRadius="lg"
                  textAlign="center"
                  borderWidth="1px"
                  borderStyle="dashed"
                >
                  <AlertCircle size={48} style={{ margin: '0 auto 16px', color: 'var(--color-gray-300)' }} />
                  <Text fontSize="lg" color="gray.600" mb={2}>
                    No reports submitted
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Reports you submit will appear here
                  </Text>
                </Box>
              ) : (
                reports.map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    onViewContent={handleViewContent}
                  />
                ))
              )}
            </VStack>
          </VStack>
        </Container>
      </Box>
    </PageWrapper>
  );
}
